import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Chip } from "@mui/material";
import { toast } from "react-toastify";
import {
  Add,
  Remove,
  DeleteOutline,
  SoupKitchen,
  RoomService,
  ReceiptLong,
  PersonAddAlt1Outlined,
  PersonOutline,
  Close as CloseIcon,
  CancelOutlined,
  DoneAll,
} from "@mui/icons-material";
import {
  incrementLine,
  decrementLine,
  removeLine,
  sendToKitchen,
  markLineServed,
  completeBill,
  discardOrder,
  selectOrderByTable,
  ITEM_STATUS,
} from "../../Store/Slices/restaurantSlice";
import {
  openCustomerDialog,
  clearSelectedCustomer,
} from "../../Store/Slices/customerSlice";
import {
  useGetAllCartItemsQuery,
  useUpdateItemStatusMutation,
} from "../../Store/Services/CartService";
import SettleBillDialog from "./SettleBillDialog";
import { clearTableCartId, getTableCartId } from "../../Utils/cartSession";
import {
  getItemTitle,
  getLineAmount,
  getOrderItems,
  summarizeOrderDetails,
} from "../../Utils/orderTotals";
import styles from "./Restaurant.module.css";

const getActiveCustomer = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = sessionStorage.getItem("posCustomer");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Item status ids shared with the kitchen and server screens:
// 1 pending, 2 sent to kitchen, 3 preparing, 4 ready, 5 serving, 6 served.
const SERVED_STATUS_ID = 6;

const statusMeta = {
  [ITEM_STATUS.DRAFT]: { label: "New", cls: styles.badgeDraft },
  [ITEM_STATUS.PREPARING]: { label: "Pending", cls: styles.badgePreparing },
  [ITEM_STATUS.IN_PROGRESS]: { label: "In Progress", cls: styles.badgeInProgress },
  [ITEM_STATUS.READY]: { label: "Ready", cls: styles.badgeReady },
  [ITEM_STATUS.SERVING]: { label: "In Progress", cls: styles.badgeInProgress },
  [ITEM_STATUS.SERVED]: { label: "Served", cls: styles.badgeServed },
};

const apiStatusMeta = {
  PENDING: { label: "New", cls: styles.badgeDraft },
  SENT_TO_KITCHEN: { label: "Pending", cls: styles.badgePreparing },
  PREPARING: { label: "In Progress", cls: styles.badgeInProgress },
  READY: { label: "Ready", cls: styles.badgeReady },
  SERVING: { label: "In Progress", cls: styles.badgeInProgress },
  SERVED: { label: "Served", cls: styles.badgeServed },
  CANCELLED: { label: "Cancelled", cls: styles.badgePreparing },
  REJECTED: { label: "Rejected", cls: styles.badgePreparing },
  REFUNDED: { label: "Refunded", cls: styles.badgePreparing },
};

const OrderPanel = ({ table }) => {
  const dispatch = useDispatch();
  const currency = useSelector((state) => state.currency);
  const order = useSelector(selectOrderByTable(table?.id));
  const [settleOpen, setSettleOpen] = useState(false);
  const [customer, setCustomer] = useState(() => getActiveCustomer());
  // A table can already carry an open order head from the table list API (an
  // order started on another terminal or in an earlier browser session), so
  // fall back to it when this session has no cart id mapped yet.
  const resolveCartId = () =>
    getTableCartId(table?.id) ||
    (table?.activeOrderHeadId ? String(table.activeOrderHeadId) : null);

  const [activeCartId, setActiveCartId] = useState(resolveCartId);
  const [updateItemStatus] = useUpdateItemStatusMutation();
  const [isCompletingOrder, setIsCompletingOrder] = useState(false);

  useEffect(() => {
    setActiveCartId(resolveCartId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.id, table?.activeOrderHeadId]);

  const { data: cartData, isSuccess: hasCartData } = useGetAllCartItemsQuery(
    activeCartId,
    { skip: !activeCartId }
  );

  useEffect(() => {
    const handleCustomerChange = (event) => {
      setCustomer(event?.detail?.customer ?? getActiveCustomer());
    };

    const handleCartIdChange = (event) => {
      const nextTableId = event?.detail?.tableId;
      if (nextTableId && String(nextTableId) !== String(table?.id)) {
        return;
      }

      setActiveCartId(resolveCartId());
    };

    window.addEventListener("pos:customer-changed", handleCustomerChange);
    window.addEventListener("pos:cart-id-changed", handleCartIdChange);

    return () => {
      window.removeEventListener("pos:customer-changed", handleCustomerChange);
      window.removeEventListener("pos:cart-id-changed", handleCartIdChange);
    };
    // The cart-id listener compares against the currently selected table, so it
    // has to be rebound when that table changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.id, table?.activeOrderHeadId]);

  const handleRemoveCustomer = () => {
    dispatch(clearSelectedCustomer());
    try {
      sessionStorage.removeItem("posCustomer");
    } catch {
      // ignore storage failures
    }
    setCustomer(null);
    window.dispatchEvent(
      new CustomEvent("pos:customer-changed", { detail: { customer: null } })
    );
  };

  const handleDiscardOrder = () => {
    if (!table?.id) return;
    dispatch(discardOrder(table.id));
  };

  const customerName =
    customer &&
    (customer.custName ||
      `${customer.custFirstName || ""} ${customer.custLastName || ""}`.trim() ||
      customer.custMobile ||
      customer.custEmail ||
      "Customer");

  const items = useMemo(() => order?.items || [], [order]);

  const draftItems = useMemo(
    () => items.filter((l) => l.status === ITEM_STATUS.DRAFT),
    [items],
  );
  const sentItems = useMemo(() => {
    const statusRank = {
      [ITEM_STATUS.PREPARING]: 0,
      [ITEM_STATUS.READY]: 0,
      [ITEM_STATUS.IN_PROGRESS]: 1,
      [ITEM_STATUS.SERVING]: 1,
      [ITEM_STATUS.SERVED]: 2,
    };

    return items
      .filter((l) => l.status !== ITEM_STATUS.DRAFT)
      .slice()
      .sort((a, b) => (statusRank[a.status] ?? 99) - (statusRank[b.status] ?? 99));
  }, [items]);

  const totals = useMemo(() => {
    const subtotal = items.reduce(
      (sum, line) =>
        sum + line.qty * line.price * (1 - (line.discount || 0) / 100),
      0
    );
    const tax = subtotal * 0.05;
    const qty = items.reduce((sum, line) => sum + line.qty, 0);
    return { subtotal, tax, total: subtotal + tax, qty };
  }, [items]);

  const hasDraft = draftItems.length > 0;
  const symbol = currency?.currencySymbol || "";

  const cartItems = useMemo(
    () => (hasCartData ? getOrderItems(cartData) : []),
    [cartData, hasCartData]
  );

  const pendingApiItems = useMemo(
    () =>
      cartItems.filter((line) => {
        const statusId = Number(line?.itemStatusId);
        const statusCode = String(line?.itemStatus || "").toUpperCase();
        return statusId === 1 || statusCode === "PENDING";
      }),
    [cartItems]
  );

  const cartSummary = useMemo(
    () => summarizeOrderDetails(cartData),
    [cartData]
  );

  const shouldUseCartFallback = Boolean(activeCartId) && cartItems.length > 0;
  const footerTotals = shouldUseCartFallback ? cartSummary : totals;

  // The bill can only be settled once every line has actually been served.
  // Anything short of that is listed here so the order can be closed off
  // deliberately instead of being paid for while food is still in the kitchen.
  const unservedApiItems = useMemo(
    () =>
      cartItems.filter((line) => {
        const statusId = Number(line?.itemStatusId);
        const statusCode = String(line?.itemStatus || "").toUpperCase();

        if (["CANCELLED", "REJECTED", "REFUNDED"].includes(statusCode)) {
          return false;
        }

        return statusId !== SERVED_STATUS_ID && statusCode !== "SERVED";
      }),
    [cartItems]
  );

  const unservedLocalItems = useMemo(
    () => items.filter((line) => line.status !== ITEM_STATUS.SERVED),
    [items]
  );

  const unservedCount = shouldUseCartFallback
    ? unservedApiItems.length
    : unservedLocalItems.length;
  const hasItems = shouldUseCartFallback
    ? cartItems.length > 0
    : items.length > 0;

  // Settling only takes the money. The order stays open until every item has
  // been served, and Complete Order is what closes it and frees the table.
  const isPaid = Boolean(order?.paidAt);
  const canCloseOrder = isPaid && unservedCount === 0;

  const handleSendToKitchenApi = async () => {
    const pending = pendingApiItems;
    if (!pending.length) return;

    await Promise.all(
      pending.map((line) =>
        updateItemStatus({
          orderDetId: line.orderDetId,
          statusId: 2,
        })
          .unwrap()
          .catch(() => null)
      )
    );

    // Advance the local mirror too. It is written when an item is added, so
    // leaving it behind here stranded every line at DRAFT.
    dispatch(sendToKitchen(table.id));
  };

  const handleServeApi = async (line) => {
    if (!line?.orderDetId) return;
    await updateItemStatus({
      orderDetId: line.orderDetId,
      statusId: SERVED_STATUS_ID,
    }).unwrap();
  };

  // Closes a paid order once serving is finished: releases the table and drops
  // the table -> cart mapping so the next sitting starts a fresh order.
  const handleCloseOrder = () => {
    if (!canCloseOrder || isCompletingOrder) return;

    setIsCompletingOrder(true);

    clearTableCartId(table?.id);
    dispatch(completeBill(table.id));
    window.dispatchEvent(
      new CustomEvent("pos:cart-id-changed", {
        detail: { cartId: null, tableId: table?.id ?? null },
      })
    );

    setIsCompletingOrder(false);
    toast.success(
      table?.isOnline
        ? `${table?.name} closed`
        : `Table ${table?.name} closed`,
      { position: "bottom-right" }
    );
  };

  const renderLine = (line) => {
    const meta = statusMeta[line.status];
    const isDraft = line.status === ITEM_STATUS.DRAFT;
    const isReady = line.status === ITEM_STATUS.READY;
    const rowStatusClass =
      line.status === ITEM_STATUS.SERVED
        ? styles.orderLineServed
        : line.status === ITEM_STATUS.IN_PROGRESS ||
          line.status === ITEM_STATUS.SERVING
        ? styles.orderLineInProgress
        : line.status === ITEM_STATUS.PREPARING ||
          line.status === ITEM_STATUS.READY
        ? `${styles.orderLinePending} ${styles.orderLinePendingBlink}`
        : "";
    return (
      <div key={line.lineId} className={`${styles.orderLine} ${rowStatusClass}`}>
        <div className={styles.orderLineMain}>
          <div className={styles.orderLineText}>
            <span className={styles.orderLineName}>{line.title}</span>
            <span className={`${styles.statusBadge} ${meta.cls}`}>
              {meta.label}
            </span>
          </div>
          <span className={styles.orderLineAmount}>
            {symbol}{" "}
            {(line.qty * line.price * (1 - (line.discount || 0) / 100)).toFixed(2)}
          </span>
        </div>
        <div className={styles.orderLineControls}>
          {isDraft ? (
            <div className={styles.qtyControl}>
              <button
                onClick={() =>
                  dispatch(decrementLine({ tableId: table.id, lineId: line.lineId }))
                }
              >
                <Remove fontSize="small" />
              </button>
              <span>{line.qty}</span>
              <button
                onClick={() =>
                  dispatch(incrementLine({ tableId: table.id, lineId: line.lineId }))
                }
              >
                <Add fontSize="small" />
              </button>
            </div>
          ) : (
            <span className={styles.qtyStatic}>Qty {line.qty}</span>
          )}

          {isReady && (
            <button
              className={styles.serveButton}
              onClick={() =>
                dispatch(markLineServed({ tableId: table.id, lineId: line.lineId }))
              }
            >
              <RoomService fontSize="small" /> Serve
            </button>
          )}

          {line.status !== ITEM_STATUS.SERVED && (
            <button
              className={styles.lineRemove}
              onClick={() =>
                dispatch(removeLine({ tableId: table.id, lineId: line.lineId }))
              }
            >
              <DeleteOutline fontSize="small" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCartLine = (line) => {
    const statusCode = String(line?.itemStatus || "").toUpperCase();
    const meta = apiStatusMeta[statusCode] || {
      label: line?.itemStatus || "Pending",
      cls: styles.badgePreparing,
    };
    const rowStatusClass =
      statusCode === "SERVED"
        ? styles.orderLineServed
        : statusCode === "PREPARING" || statusCode === "SERVING"
        ? styles.orderLineInProgress
        : statusCode === "SENT_TO_KITCHEN" || statusCode === "READY"
        ? `${styles.orderLinePending} ${styles.orderLinePendingBlink}`
        : "";

    return (
    <div
      key={line.orderDetId || `${line.title}-${line.qty}`}
      className={`${styles.orderLine} ${rowStatusClass}`}
    >
      <div className={styles.orderLineMain}>
        <div className={styles.orderLineText}>
          <span className={styles.orderLineName}>{getItemTitle(line)}</span>
          <span className={`${styles.statusBadge} ${meta.cls}`}>{meta.label}</span>
          <span className={styles.qtyStatic}>Qty {line.qty}</span>
        </div>
        <span className={styles.orderLineAmount}>
          {symbol} {getLineAmount(line).toFixed(2)}
        </span>
      </div>
      {(statusCode === "READY" || statusCode === "SERVING") && (
        <div className={styles.orderLineControls}>
          <button className={styles.serveButton} onClick={() => handleServeApi(line)}>
            <RoomService fontSize="small" /> Serve
          </button>
        </div>
      )}
    </div>
    );
  };

  return (
    <div className={styles.orderPane}>
      <div className={styles.orderHeader}>
        <div>
          <span className={styles.orderTableLabel}>
            {table?.isOnline ? table.name : `Table ${table?.name}`}
          </span>
          {hasItems && (
            <div className={styles.orderStateTags}>
              {isPaid && (
                <span
                  className={`${styles.orderStateTag} ${styles.orderStateTagPaid}`}
                >
                  Paid
                  {order?.paymentMethod
                    ? ` · ${String(order.paymentMethod).toUpperCase()}`
                    : ""}
                </span>
              )}
              <span
                className={`${styles.orderStateTag} ${
                  unservedCount > 0
                    ? styles.orderStateTagPending
                    : styles.orderStateTagReady
                }`}
              >
                {unservedCount > 0
                  ? `${unservedCount} item${
                      unservedCount === 1 ? "" : "s"
                    } to serve`
                  : "All items served"}
              </span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {table?.isOnline && (
            <Button
              variant="outlined"
              size="large"
              color="error"
              startIcon={
                <CancelOutlined sx={{ fontSize: "1.6rem !important" }} />
              }
              onClick={handleDiscardOrder}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                py: 0.8,
                px: 2,
                borderRadius: 2,
              }}
            >
              Discard
            </Button>
          )}
          {customer ? (
            <Chip
              icon={<PersonOutline />}
              label={customerName}
              onClick={() => dispatch(openCustomerDialog())}
              onDelete={handleRemoveCustomer}
              deleteIcon={<CloseIcon />}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ maxWidth: 200, fontWeight: 600 }}
            />
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={
                <PersonAddAlt1Outlined sx={{ fontSize: "1.6rem !important" }} />
              }
              onClick={() => dispatch(openCustomerDialog())}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                py: 0.8,
                px: 2,
                borderRadius: 2,
                backgroundColor: "#0f766e",
                "&:hover": { backgroundColor: "#0b5952" },
              }}
            >
              Customer
            </Button>
          )}
        </div>
      </div>

      <div className={styles.orderBody}>
        {shouldUseCartFallback && (
          <div className={styles.orderSection}>
            {cartItems.map(renderCartLine)}
          </div>
        )}

        {!shouldUseCartFallback && items.length === 0 && (
          <p className={styles.orderEmpty}>
            No items yet. Pick items from the menu to start the order.
          </p>
        )}

        {!shouldUseCartFallback && sentItems.length > 0 && (
          <div className={styles.orderSection}>
            {sentItems.map(renderLine)}
          </div>
        )}

        {!shouldUseCartFallback && draftItems.length > 0 && (
          <div className={styles.orderSection}>
            <p className={styles.orderSectionTitle}>New Items</p>
            {draftItems.map(renderLine)}
          </div>
        )}
      </div>

      <div className={styles.orderFooter}>
        <div className={styles.totalsRow}>
          <span>Qty {footerTotals.qty}</span>
          <span>
            Subtotal {symbol} {footerTotals.subtotal.toFixed(2)}
          </span>
        </div>
        <div className={styles.totalsRow}>
          <span>Tax</span>
          <span>
            {symbol} {footerTotals.tax.toFixed(2)}
          </span>
        </div>
        <div className={styles.grandTotalRow}>
          <span>Total</span>
          <span>
            {symbol} {footerTotals.total.toFixed(2)}
          </span>
        </div>

        <div className={styles.orderActions}>
          <button
            className={styles.kitchenButton}
            disabled={shouldUseCartFallback ? pendingApiItems.length === 0 : !hasDraft}
            onClick={() =>
              shouldUseCartFallback
                ? handleSendToKitchenApi()
                : dispatch(sendToKitchen(table.id))
            }
          >
            <SoupKitchen fontSize="small" /> Send to Kitchen
          </button>
          <button
            className={styles.billButton}
            disabled={!hasItems || isPaid}
            onClick={() => setSettleOpen(true)}
            title={isPaid ? "This order has already been paid." : undefined}
          >
            <ReceiptLong fontSize="small" />{" "}
            {isPaid ? "Bill Paid" : "Settle Bill"}
          </button>

          {isPaid && (
            <button
              className={styles.completeOrderButton}
              onClick={handleCloseOrder}
              disabled={!canCloseOrder || isCompletingOrder}
              title={
                canCloseOrder
                  ? undefined
                  : `${unservedCount} item${
                      unservedCount === 1 ? "" : "s"
                    } still to serve.`
              }
            >
              <DoneAll fontSize="small" />{" "}
              {canCloseOrder
                ? "Complete Order"
                : `Complete Order (${unservedCount} to serve)`}
            </button>
          )}
        </div>
      </div>

      <SettleBillDialog
        open={settleOpen}
        onClose={() => setSettleOpen(false)}
        table={table}
        order={order}
      />
    </div>
  );
};

export default OrderPanel;
